import { DetailPanelSkeleton } from '@/components/compliance/detail-primitives'
import { PAGE_HEADER_ACTIONS_SLOT_ID, PAGE_HEADER_BREADCRUMB_SLOT_ID } from '@/lib/page-header'
import { useQuery } from '@tanstack/react-query'
import { Navigate, Outlet, useLocation, useParams } from '@tanstack/react-router'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { ScreenataPageLayout } from '@/components/compliance/ScreenataPageLayout'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { Eye } from 'lucide-react'
import {
  BASE_CHROME_HEIGHT,
  usePublishViewModeBanner,
} from '@/components/ViewModeBanner'
import {
  AdminAuditorViewExitLink,
} from './AdminAuditorViewBanner'
import { AuditorCycleBreadcrumb } from './AuditorCycleBreadcrumb'
import {
  AuditorControlsSkeleton,
  AuditorCycleGenericSkeleton,
  AuditorEvidenceSkeleton,
  AuditorOpenRequestsSkeleton,
  AuditorPeopleSkeleton,
  AuditorRequestListSkeleton,
  AuditorTimelineSkeleton,
} from './AuditorCycleRouteSkeletons'
import { AuditorCycleTopBar } from './AuditorCycleTopBar'
import { AuditorControlsExportButton } from '@/components/compliance/ExportControlsButton'
import {
  AuditorCycleBreadcrumbSkeleton,
  AuditorDashboardSkeleton,
} from './AuditorDashboardSkeleton'

export type AuditorCenterCycleContextValue = {
  dashboard: any
  isAuditorReadOnly?: boolean
  adminAuditorPreview?: {
    workspaceId: string
    auditCycleId: string
  }
}

const AuditorCenterCycleContext =
  createContext<AuditorCenterCycleContextValue | null>(null)

export function AuditorCenterCycleProvider({
  value,
  children,
}: {
  value: AuditorCenterCycleContextValue
  children: ReactNode
}) {
  return (
    <AuditorCenterCycleContext.Provider value={value}>
      {children}
    </AuditorCenterCycleContext.Provider>
  )
}

/**
 * Keyed on the ROUTE SEGMENT, not the breadcrumb label: a page rename would
 * otherwise silently drop that route to the generic skeleton, with no type
 * error and no failing test to catch it.
 */
function getAuditorCyclePendingContent(segment: string) {
  switch (segment) {
    case 'dashboard':
      return <AuditorDashboardSkeleton />
    // These two set `pageHeader.pad: false`, so their skeletons own their
    // padding (or are deliberately full-bleed). The generic fallback does not,
    // and would flash flush-to-the-edge before snapping into the page.
    case 'open-requests':
      return <AuditorOpenRequestsSkeleton />
    case 'request-list':
      return <AuditorRequestListSkeleton />
    case 'people':
      return <AuditorPeopleSkeleton />
    case 'timeline':
      return <AuditorTimelineSkeleton />
    case 'evidence':
    case 'audit-evidence':
    case 'evidence-detail':
      return <AuditorEvidenceSkeleton />
    case 'controls':
      return <AuditorControlsSkeleton />
    default:
      return <AuditorCycleGenericSkeleton />
  }
}

export function AuditorCenterCycleShell() {
  const { workspaceId, auditCycleId, employeeId, providerSlug } = useParams({
    strict: false,
  })
  const { pathname } = useLocation()
  const isIntegrationRoute = pathname.includes('/integrations')
  const { isAuditor, isAdmin, isOwner, isLoading } =
    useWorkspacePermissions(workspaceId)
  const dashboardQuery = useQuery(
    trpc.auditor.getDashboard.queryOptions({ auditCycleId: auditCycleId! })
  )

  // Route segment — drives the pending skeleton (must be before early returns).
  const currentSegment = useMemo(() => {
    if (pathname.includes('/audit-evidence/')) return 'evidence-detail'
    if (pathname.includes('/vendors/')) return pathname.includes('/reviews/') ? 'vendor-review-detail' : 'vendor-detail'
    if (pathname.includes('/risks/')) return 'risk-detail'
    if (pathname.includes('/assets/')) return 'asset-detail'
    if (pathname.includes('/populations/')) return pathname.includes('/rounds/') ? 'population-round' : 'population-detail'
    return pathname.split('/').pop() ?? ''
  }, [pathname])

  const isControlsRoute = currentSegment === 'controls'
  const isRegisterDetail = [
    'vendor-detail',
    'vendor-review-detail',
    'risk-detail',
    'asset-detail',
  ].includes(currentSegment)
  const isRegisterList = ['vendors', 'risks', 'assets'].includes(currentSegment)
  const isRegisterEntityDetail = [
    'vendor-detail',
    'risk-detail',
    'asset-detail',
  ].includes(currentSegment)

  // Breadcrumb label for that segment.
  const currentPage = useMemo(() => {
    if (currentSegment === 'evidence-detail') return 'Evidence Detail'
    const pageMap: Record<string, string> = {
      dashboard: 'Dashboard',
      'api-access': 'API access',
      vendors: 'Vendors',
      'vendor-detail': 'Vendor detail',
      'vendor-review-detail': 'Review detail',
      risks: 'Risks',
      'risk-detail': 'Risk detail',
      assets: 'Assets',
      'asset-detail': 'Asset detail',
      controls: 'Controls',
      'audit-evidence': 'Evidence',
      evidence: 'Evidence',
      'open-requests': 'Inbox',
      'request-list': 'Information Requests',
      policies: 'Policies',
      people: 'People',
      populations: 'Populations',
      'population-detail': 'Population',
      'population-round': 'Sampling round',
      integrations: 'Integrations',
      timeline: 'Timeline',
    }
    return pageMap[currentSegment] ?? currentSegment
  }, [currentSegment])

  const isShellBlocking = isLoading || dashboardQuery.isPending

  // Computed once, above every early return: the publish hook cannot sit inside
  // a branch. `!isLoading` keeps the banner from flashing before permissions
  // resolve — claiming "you are impersonating" and then retracting it is worse
  // than a moment with no banner.
  const showAdminAuditorViewHeader = !isLoading && (isAdmin || isOwner) && !isAuditor

  usePublishViewModeBanner(
    'preview',
    showAdminAuditorViewHeader
      ? {
          icon: Eye,
          label: 'Auditor preview',
          description: 'This is what your auditor sees on your submission',
          action: (
            <AdminAuditorViewExitLink
              workspaceId={workspaceId!}
              auditCycleId={auditCycleId!}
            />
          ),
        }
      : null,
    [showAdminAuditorViewHeader, workspaceId, auditCycleId],
  )

  if (isShellBlocking) {

    // Reserve the banner's space while loading, so the chrome height does not
    // change under the panels once the dashboard resolves — otherwise the
    // full-height lists jump by the banner's height on first paint.
    return (
      <div
        className={isControlsRoute ? 'flex h-full min-h-0 flex-col overflow-hidden' : isRegisterDetail ? 'flex h-full min-h-0 flex-col' : 'h-full'}
        style={{
          ['--app-chrome-h' as string]: BASE_CHROME_HEIGHT,
        }}
      >
        <ScreenataPageLayout
          title='Audits'
          customHeader={
            <AuditorCycleTopBar breadcrumb={<AuditorCycleBreadcrumbSkeleton />} />
          }
          scroll={!isControlsRoute && !employeeId}
          raw={isControlsRoute || isIntegrationRoute}
          mainClassName={isControlsRoute ? 'flex min-h-0 overflow-hidden' : undefined}
        >
          {employeeId ? <DetailPanelSkeleton /> : getAuditorCyclePendingContent(currentSegment)}
        </ScreenataPageLayout>
      </div>
    )
  }

  if (!isAuditor && !isAdmin && !isOwner) {
    return (
      <Navigate
        to='/workspaces/$workspaceId/auditor-center'
        params={{ workspaceId: workspaceId! }}
        replace
      />
    )
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <ScreenataPageLayout title='Audits'>
        <div className='rounded-sm border border-border bg-card p-8 text-[13px] text-text-subtle'>
          Unable to load this audit.
        </div>
      </ScreenataPageLayout>
    )
  }

  const isAuditorReadOnly =
    isAuditor && dashboardQuery.data.cycle?.status === 'completed'
  const auditsHomeRoute =
    isAdmin || isOwner
      ? '/workspaces/$workspaceId/audits'
      : '/workspaces/$workspaceId/auditor-center'
  const isIntegrationDetail = Boolean(providerSlug)

  const breadcrumb = (
    <AuditorCycleBreadcrumb
      workspaceId={workspaceId!}
      auditsHomeRoute={auditsHomeRoute}
      items={
        !employeeId && !isIntegrationDetail && !isRegisterEntityDetail && currentPage
          ? [{ label: currentPage, current: true }]
          : []
      }
    />
  )

  // The preview notice moved OUT of the header's trailing slot and into a
  // full-width banner below. The header keeps the breadcrumb plus whatever the
  // current page contributes — the Controls review surface is full-bleed and
  // owns no header of its own, so its Excel export hangs here.
  const auditHeader = (
    <AuditorCycleTopBar
      breadcrumb={
        <>
          {breadcrumb}
          {(employeeId || isIntegrationDetail || isRegisterEntityDetail) && (
            <div id={PAGE_HEADER_BREADCRUMB_SLOT_ID} />
          )}
        </>
      }
      actions={
        employeeId ? <div id={PAGE_HEADER_ACTIONS_SLOT_ID} /> : currentSegment === 'controls' ? (
          <AuditorControlsExportButton auditCycleId={auditCycleId!} />
        ) : null
      }
    />
  )

  return (
    <AuditorCenterCycleProvider
      value={{ dashboard: dashboardQuery.data, isAuditorReadOnly }}
    >
      {/* `--app-chrome-h` is the header + tab-bar height for the panels below,
          which subtract it from `--app-vh` rather than from the viewport. The
          banner is NOT in this number — `--app-vh` already excludes it, and
          counting it twice would leave those panels 2rem short. The banner is
          rendered by `<ViewModeBannerSlot />` above the sidebar; see
          `usePublishViewModeBanner` for why it cannot be rendered here. */}
      <div
        className={isControlsRoute ? 'flex h-full min-h-0 flex-col overflow-hidden' : isRegisterDetail ? 'flex h-full min-h-0 flex-col' : 'h-full'}
        style={{
          ['--app-chrome-h' as string]: BASE_CHROME_HEIGHT,
        }}
      >
        <ScreenataPageLayout title='Audits' customHeader={auditHeader}
          scroll={!isControlsRoute && !isRegisterDetail && !employeeId}
          raw={isControlsRoute || isRegisterDetail || isRegisterList || Boolean(employeeId) || isIntegrationRoute}
          mainClassName={isControlsRoute || isRegisterDetail ? 'flex min-h-0 overflow-hidden' : undefined}
        >
          <Outlet />
        </ScreenataPageLayout>
      </div>
    </AuditorCenterCycleProvider>
  )
}

export function useAuditorCenterCycle() {
  const context = useContext(AuditorCenterCycleContext)

  if (!context) {
    throw new Error(
      'useAuditorCenterCycle must be used within AuditorCenterCycleShell'
    )
  }

  return context
}

export function useAuditorCenterAdminPreview() {
  return useContext(AuditorCenterCycleContext)?.adminAuditorPreview
}
